import type { TFunction } from 'i18next'
import type { Severity } from './api'

const SEVERITY_RANK: Record<Severity, number> = { high: 0, medium: 1, low: 2 }

/**
 * Within a level: by severity (high, medium, low), then by count, largest first - the contract's
 * order. The server sends them so; sorting again here means a panel never depends on it.
 */
export function orderFindings<T extends { severity: Severity; count: number }>(items: readonly T[]): T[] {
    return [...items].sort(
        (a, b) => (SEVERITY_RANK[a.severity] ?? 3) - (SEVERITY_RANK[b.severity] ?? 3) || b.count - a.count,
    )
}

/** The fixed rules' locale keys. RuleIDs are snake_case; i18n keys stay camelCase like the rest. */
const RULE_KEYS: Record<string, string> = {
    service_without_application: 'serviceWithoutApplication',
    application_without_platform: 'applicationWithoutPlatform',
    rank_violation: 'rankViolation',
    retired_classification: 'retiredClassification',
    no_recovery_time: 'noRecoveryTime',
    no_backup_copy: 'noBackupCopy',
    no_runbook: 'noRunbook',
    no_team: 'noTeam',
    no_site: 'noSite',
}

export const CAPABILITY_PREFIX = 'missing_capability.'

/**
 * A rule's name for people. A capability rule names its capability from the server's list
 * (HierarchyCapabilities.Name - data, so not translated here); an unknown rule shows its id rather
 * than nothing, so a rule added on the server is still visible before the client learns it.
 */
export function ruleLabel(t: TFunction, ruleId: string, capabilityName?: string | null): string {
    if (ruleId.startsWith(CAPABILITY_PREFIX)) {
        const name = capabilityName || ruleId.slice(CAPABILITY_PREFIX.length)
        return t('hierarchyAdvisor.rules.missingCapability', { capability: name })
    }
    const key = RULE_KEYS[ruleId]
    return key ? t(`hierarchyAdvisor.rules.${key}`) : ruleId
}
